import {
  type Meeting as StoreMeeting,
  type Thread,
  useStores,
} from '@littlebird/sdk';
import {
  collectUpcomingMeetingIds,
  getMeetingBucketDate,
  getMeetingSortDate,
  type ProjectActivityFilter,
} from '@littlebird/ui-utils';
import { format } from 'date-fns/format';
import { useCallback, useDeferredValue, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useChatListPagination } from '@/features/chat/all-chats/all-chats.hooks';
import { filterChatsByTitle } from '@/features/chat/all-chats/all-chats.utils';
import { ALL_MEETINGS_FILTER } from '@/features/meetings/meetings-filter';
import { useMeetingListPagination } from '@/features/meetings/meetings-list-pagination';
import { filterMeetingIdsBySearch } from '@/features/meetings/meetings-list.utils';
import { useUpcomingMeetings } from '@/features/meetings/meetings.hooks';
import { meetingApis } from '@/features/meetings/meetings.query';
import { type CalendarEvent } from '@/features/meetings/meetings.types';
import { useScrollParentSentinelLoadMore } from '@/hooks/use-scroll-parent-sentinel-load-more';
import { pluralize } from '@/lib/date-utils';

import {
  type ProjectAssignmentTarget,
  useProjectActions,
} from './project-actions';

const DAY_KEY_FORMAT = 'yyyy-MM-dd';

interface PickerItem {
  key: string;
  index: number;
  target: ProjectAssignmentTarget;
}

interface PickerSection {
  date: string;
  items: PickerItem[];
}

function getTargetKey(target: ProjectAssignmentTarget): string {
  return `${target.kind}:${target.id}`;
}

function getItemsNoun(chatCount: number, noteCount: number): string {
  if (chatCount === 0) return pluralize(noteCount, 'note');
  if (noteCount === 0) return pluralize(chatCount, 'chat');
  return 'items';
}

function buildDayBuckets(
  chats: Thread[],
  meetings: StoreMeeting[],
  eventsById: ReadonlyMap<string, CalendarEvent>
): { date: string; targets: ProjectAssignmentTarget[] }[] {
  const buckets = new Map<
    string,
    { target: ProjectAssignmentTarget; sortTimestamp: number }[]
  >();

  const addToBucket = (
    target: ProjectAssignmentTarget,
    bucketDate: Date,
    sortDate: Date
  ) => {
    const day = format(bucketDate, DAY_KEY_FORMAT);
    const entry = { target, sortTimestamp: sortDate.getTime() };
    const bucket = buckets.get(day);
    if (bucket) bucket.push(entry);
    else buckets.set(day, [entry]);
  };

  for (const chat of chats) {
    const lastActivityAt = new Date(chat.lastMessageAt ?? chat.createdAt);
    addToBucket(
      { kind: 'thread', id: chat.id },
      lastActivityAt,
      lastActivityAt
    );
  }

  for (const meeting of meetings) {
    const eventStartAt = meeting.eventId
      ? eventsById.get(meeting.eventId)?.start_datetime
      : undefined;
    addToBucket(
      { kind: 'meeting', id: meeting.id },
      getMeetingBucketDate(meeting, eventStartAt),
      getMeetingSortDate(meeting, eventStartAt)
    );
  }

  return [...buckets.entries()]
    .sort(([leftDay], [rightDay]) => rightDay.localeCompare(leftDay))
    .map(([date, entries]) => ({
      date,
      targets: entries
        .sort((left, right) => right.sortTimestamp - left.sortTimestamp)
        .map((entry) => entry.target),
    }));
}

export function useExistingItemsPicker(projectId: string, onDone: () => void) {
  const { meetings, projects, threads } = useStores();
  const { setProjectAssignment } = useProjectActions();
  const { data: calendarEvents = [] } = meetingApis.useCalendarEventsQuery();
  const { upcomingMeetingsWithStatus } = useUpcomingMeetings();

  const [filter, setFilter] = useState<ProjectActivityFilter>('all');
  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search);
  const [selection, setSelection] = useState<
    ReadonlyMap<string, ProjectAssignmentTarget>
  >(new Map());
  const [isSubmitting, setIsSubmitting] = useState(false);

  const chatPagination = useChatListPagination('all');
  const meetingPagination = useMeetingListPagination(ALL_MEETINGS_FILTER);

  const trimmedSearch = deferredSearch.trim();
  const hasSearchQuery = trimmedSearch.length > 0;

  const { sentinelRef: chatSentinelRef } = useScrollParentSentinelLoadMore({
    enabled: chatPagination.isLoaded && !hasSearchQuery,
    hasMorePages: chatPagination.hasMore,
    loadNextPage: chatPagination.loadNextPage,
  });
  const { sentinelRef: meetingSentinelRef } = useScrollParentSentinelLoadMore({
    enabled: meetingPagination.isLoaded && !hasSearchQuery,
    hasMorePages: meetingPagination.hasMore,
    loadNextPage: meetingPagination.loadNextPage,
  });

  const eventsById = useMemo(
    () => new Map(calendarEvents.map((event) => [event.id, event])),
    [calendarEvents]
  );

  const upcomingMeetingIds = collectUpcomingMeetingIds(
    upcomingMeetingsWithStatus
  );
  const eligibleChats = threads
    .getChatsForFilter('all')
    .filter((chat) => chat.projectId !== projectId);
  const eligibleMeetings = meetings
    .getMeetingsForFilter(ALL_MEETINGS_FILTER)
    .filter(
      (meeting) =>
        meeting.projectId !== projectId && !upcomingMeetingIds.has(meeting.id)
    );

  const matchedMeetingIds = hasSearchQuery
    ? new Set(
        filterMeetingIdsBySearch(
          eligibleMeetings.map((meeting) => meeting.id),
          trimmedSearch,
          meetings,
          calendarEvents
        )
      )
    : null;
  const matchedMeetings = matchedMeetingIds
    ? eligibleMeetings.filter((meeting) => matchedMeetingIds.has(meeting.id))
    : eligibleMeetings;

  const dayBuckets = buildDayBuckets(
    filter === 'meetings'
      ? []
      : filterChatsByTitle(eligibleChats, trimmedSearch),
    filter === 'chats' ? [] : matchedMeetings,
    eventsById
  );
  const flatTargets = dayBuckets.flatMap((bucket) => bucket.targets);

  let dayStartIndex = 0;
  const sections: PickerSection[] = dayBuckets.map((bucket) => {
    const items = bucket.targets.map((target, indexInDay) => ({
      key: getTargetKey(target),
      index: dayStartIndex + indexInDay,
      target,
    }));
    dayStartIndex += bucket.targets.length;
    return { date: bucket.date, items };
  });

  const eligibleIds = {
    thread: new Set(eligibleChats.map((chat) => chat.id)),
    meeting: new Set(eligibleMeetings.map((meeting) => meeting.id)),
  };
  const validTargets = [...selection.values()].filter((target) =>
    eligibleIds[target.kind].has(target.id)
  );
  const selectedChatCount = validTargets.filter(
    (target) => target.kind === 'thread'
  ).length;
  const itemsNoun = getItemsNoun(
    selectedChatCount,
    validTargets.length - selectedChatCount
  );

  const toggleTarget = useCallback((target: ProjectAssignmentTarget) => {
    setSelection((previousSelection) => {
      const nextSelection = new Map(previousSelection);
      const key = getTargetKey(target);
      if (nextSelection.has(key)) nextSelection.delete(key);
      else nextSelection.set(key, target);
      return nextSelection;
    });
  }, []);

  const confirmAdd = async () => {
    if (isSubmitting || validTargets.length === 0) return;
    setIsSubmitting(true);
    try {
      const results = await Promise.all(
        validTargets.map((target) => setProjectAssignment(target, projectId))
      );
      const successCount = results.filter(Boolean).length;
      if (successCount === validTargets.length) {
        toast.success(
          `Added ${successCount} ${itemsNoun} to ${projects.getById(projectId)?.name ?? 'project'}`
        );
      }
      onDone();
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    activateIndex: (index: number) => toggleTarget(flatTargets[index]),
    chatSentinelRef,
    confirmAdd,
    confirmLabel:
      validTargets.length === 0
        ? 'Add'
        : `Add ${validTargets.length} ${itemsNoun}`,
    eventsById,
    filter,
    hasSearchQuery,
    isSelected: (target: ProjectAssignmentTarget) =>
      selection.has(getTargetKey(target)),
    isLoading: !chatPagination.isLoaded || !meetingPagination.isLoaded,
    isSubmitting,
    itemCount: flatTargets.length,
    meetingSentinelRef,
    search,
    sections,
    selectedCount: validTargets.length,
    setFilter,
    setSearch,
    toggleTarget,
  };
}
